import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ProgressChart } from "@/components/stats/ProgressChart";
import { CATEGORY_OPTIONS, DIFFICULTY_OPTIONS } from "@/constants/filters";
import { selectClass } from "@/constants/styles";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useProgress } from "@/hooks/use-progress";
import type { UserProgress } from "@/types";
import { getErrorMessage } from "@/utils/error-message";

type ResultFilter = "" | "correct" | "incorrect";

const resultFilters: { value: ResultFilter; label: string }[] = [
  { value: "", label: "All Results" },
  { value: "correct", label: "Correct" },
  { value: "incorrect", label: "Incorrect" },
];

function categoryBadgeColor(category: string | null): "blue" | "purple" | "green" | "red" | "yellow" | "gray" {
  switch (category) {
    case "batting": return "blue";
    case "baserunning": return "purple";
    case "fielding": return "green";
    case "pitching": return "red";
    case "situational": return "yellow";
    default: return "gray";
  }
}

export function Progress() {
  const [cursor, setCursor] = useState<string | undefined>();
  const [categoryFilter, setCategoryFilter] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("");
  const [resultFilter, setResultFilter] = useState<ResultFilter>("");
  const { data, isLoading, error } = useProgress(100, cursor);
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.items.filter((item: UserProgress) => {
      if (categoryFilter && item.category !== categoryFilter) return false;
      if (difficultyFilter && item.difficulty !== difficultyFilter) return false;
      if (resultFilter === "correct" && !item.correct) return false;
      if (resultFilter === "incorrect" && item.correct) return false;
      return true;
    });
  }, [data, categoryFilter, difficultyFilter, resultFilter]);

  const hasActiveFilters = categoryFilter || difficultyFilter || resultFilter;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-navy-700">Answer History</h1>

      <div className="flex flex-col gap-3 md:flex-row">
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className={selectClass}>
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <select value={difficultyFilter} onChange={(e) => setDifficultyFilter(e.target.value)} className={selectClass}>
          {DIFFICULTY_OPTIONS.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
        <select value={resultFilter} onChange={(e) => setResultFilter(e.target.value as ResultFilter)} className={selectClass}>
          {resultFilters.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        {hasActiveFilters && (
          <button
            onClick={() => { setCategoryFilter(""); setDifficultyFilter(""); setResultFilter(""); }}
            className="min-h-[44px] text-sm font-medium text-navy-600 hover:text-navy-800"
          >
            Clear filters
          </button>
        )}
      </div>

      {isLoading && <LoadingSpinner message="Loading history..." />}
      {error && <ErrorMessage message={getErrorMessage(error)} />}

      {data && data.items.length > 0 && (
        <>
          <Card>
            <h2 className="mb-3 text-sm font-medium text-gray-500">Recent Answers</h2>
            <ProgressChart history={data.items} />
          </Card>

          {hasActiveFilters && (
            <p className="text-sm text-gray-500">
              Showing {filtered.length} of {data.items.length} answers
            </p>
          )}

          <div className="space-y-3">
            {filtered.map((item, i) => (
              <Card
                key={`${item.scenarioId}-${i}`}
                className="flex min-h-[44px] cursor-pointer items-center justify-between gap-4 transition-colors hover:bg-gray-50"
                onClick={() => navigate(`/review/${item.scenarioId}`)}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {item.scenarioTitle}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-400">
                      {new Date(item.answeredAt).toLocaleDateString()}
                    </span>
                    {item.category && (
                      <Badge color={categoryBadgeColor(item.category)}>
                        {item.category}
                      </Badge>
                    )}
                    {item.difficulty && (
                      <Badge color="gray">{item.difficulty}</Badge>
                    )}
                  </div>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium
                    ${item.correct ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                >
                  {item.correct ? "Correct" : "Incorrect"}
                </span>
              </Card>
            ))}
          </div>

          {filtered.length === 0 && hasActiveFilters && (
            <Card className="text-center">
              <p className="text-gray-500">No answers match your filters.</p>
            </Card>
          )}

          {data.nextCursor && (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={() => setCursor(data.nextCursor ?? undefined)}
              >
                Load More
              </Button>
            </div>
          )}
        </>
      )}

      {data && data.items.length === 0 && (
        <Card className="text-center">
          <p className="text-gray-500">No answers yet. Start playing to see your progress!</p>
        </Card>
      )}
    </div>
  );
}
